import { Component } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { switchMap } from 'rxjs/operators';
import { throwError } from 'rxjs';
import moment from 'moment';
import { Boat, SlingConfig } from 'src/app/models';
import { MovementPlanByObjectiveCreateDTO } from 'src/app/models/dtos/movements/movement-plan-by-objective-createDTO';
import { ToastService } from 'src/app/services/toast.service';
import { MovementPlanService } from 'src/app/services/movements/movement-plan.service';
import { MovementExecutionService } from 'src/app/services/movements/movement-execution.service';
import { ExecuteOperationDTO } from 'src/app/models/dtos/movements/execute-operationDTO';
import { MovementOperation } from 'src/app/models/enums/movements/movement-operation';
import { MovementLocationType } from 'src/app/models/enums/movements/movement-location-type';
import { resolveLocationFinish } from '../berth-map-finish-location';
import { findOpenPlanOfDay, OPEN_PLAN_TODAY } from '../berth-map-open-plan';

/** Mensagem quando o plano foi criado, mas a saída não pôde ser apontada pelo mapa. */
export const NAVIGATION_FINISH_PENDING =
  'Plano de navegação criado, mas a saída não foi apontada pelo mapa. Conclua pela tela de Movimentação.';

export const RETURN_BEFORE_EXIT = 'A data de retorno deve ser maior que a data de saída (agora).';

/** Resultado do diálogo (undefined = cancelado ou nada criado). */
export interface NavigationPlanResult {
  planCreated: boolean;
  navigationStarted: boolean;
}

class OpenPlanOfDayError extends Error {}

/**
 * EM-2589: "Apontar Navegação" no mapa de vagas. Reaproveita o formulário de plano de navegação da movimentação
 * (`app-movement-navigation-plan`), cria o plano com objetivo NAVIGATION (POST /secure/movement-plan/objective) e
 * finaliza a etapa vaga molhada → navegação (POST /secure/movement/movement-execution/execute-operation, FINISH),
 * com o local de navegação da própria marina vindo dos destinos da etapa.
 *
 * Depois que o plano existe o diálogo sempre fecha, mesmo se a finalização falhar, para um segundo clique não criar
 * outro plano para o mesmo barco; e não cria plano se o barco já tiver plano aberto no dia (regra do legado, BUG-1063).
 * No mapa a navegação é "agora": a saída é o momento do Salvar, com o campo travado no formulário.
 */
@Component({
  selector: 'app-berth-map-navigation-plan',
  templateUrl: './berth-map-navigation-plan.component.html',
  styleUrls: ['./berth-map-navigation-plan.component.scss']
})
export class BerthMapNavigationPlanComponent {
  isNew = true;
  hasNavigationPlan = true;
  movementPlan: MovementPlanByObjectiveCreateDTO;
  slingConfig: SlingConfig;
  boat: Boat;
  saving = false;

  constructor(
    public dialogRef: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private toastService: ToastService,
    private movementPlanService: MovementPlanService,
    private movementExecutionService: MovementExecutionService
  ) {
    const data = this.config.data || {};
    this.boat = data.boat;
    this.slingConfig = data.slingConfig;
    this.movementPlan = new MovementPlanByObjectiveCreateDTO();
    this.movementPlan.boat = this.boat;
    this.movementPlan.executionDate = new Date();
  }

  finish(event: MovementPlanByObjectiveCreateDTO): void {
    if (this.saving) {
      return;
    }
    const customer = this.responsibleCustomer();
    if (!customer) {
      this.toastService.error('Embarcação sem cliente vinculado: não é possível apontar a navegação.');
      return;
    }
    // Saída = agora, mesmo que o formulário mostre outra hora: o FINISH logo abaixo põe o barco na navegação já.
    const now = new Date();
    if (!event.returnTime || !moment(event.returnTime).isAfter(now)) {
      this.toastService.error(RETURN_BEFORE_EXIT);
      return;
    }
    this.saving = true;
    const reqBody: MovementPlanByObjectiveCreateDTO = {
      id: null,
      boat: { id: this.boat.id },
      customer: { id: customer.id },
      sailor: null,
      navigationPlan: event.navigationPlan,
      // Mesmo formato do formulário de movimentação: hora local da marina, sem fuso.
      executionDate: this.toLocalDateTime(now),
      returnTime: this.toLocalDateTime(event.returnTime),
      objectiveCode: 'NAVIGATION',
      questionnairesAnswer: [],
      observation: event.observation
    };

    findOpenPlanOfDay(this.movementPlanService, this.boat.id, now).pipe(
      switchMap((openPlan) => openPlan
        ? throwError(() => new OpenPlanOfDayError(OPEN_PLAN_TODAY))
        : this.movementPlanService.saveByObjective(reqBody))
    ).subscribe({
      next: (response) => this.startNavigation(response),
      error: (err) => {
        this.saving = false;
        if (err instanceof OpenPlanOfDayError) {
          this.toastService.warning(OPEN_PLAN_TODAY);
          this.dialogRef.close();
          return;
        }
        // inclui a recusa do backend (trava do BUG-1063 quando portada): mostra a mensagem dele
        this.toastService.error(this.errorMessage(err, 'Não foi possível iniciar a navegação.'));
      }
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  previouPage(): void {
    this.close();
  }

  private startNavigation(response: any): void {
    const plan = response?.movementPlan;
    const movement = (plan?.movements || []).find(
      (m) => m.movementStatus === 'AWAITING_PREPARATION' && m.destinyLocationType === MovementLocationType.NAVIGATION
    );
    if (!plan?.id || !movement?.id) {
      this.toastService.warning(NAVIGATION_FINISH_PENDING);
      this.closeWith(false);
      return;
    }

    this.movementExecutionService.getDestinies(plan.id, movement.id).pipe(
      switchMap((destinies) => {
        const locationFinish = resolveLocationFinish(destinies);
        if (!locationFinish) {
          return throwError(() => new Error(NAVIGATION_FINISH_PENDING));
        }
        const operation: ExecuteOperationDTO = {
          movementPlanId: plan.id,
          movementId: movement.id,
          operation: MovementOperation.FINISH,
          locationFinish
        };
        return this.movementExecutionService.executeOperation(operation);
      })
    ).subscribe({
      next: () => {
        this.toastService.success('Embarcação em navegação.');
        this.closeWith(true);
      },
      error: (err) => {
        this.toastService.error(this.errorMessage(err, NAVIGATION_FINISH_PENDING));
        this.closeWith(false);
      }
    });
  }

  /** Plano criado; `navigationStarted` diz se a saída foi apontada (senão o mapa não oferece de novo). */
  private closeWith(navigationStarted: boolean): void {
    const result: NavigationPlanResult = { planCreated: true, navigationStarted };
    this.dialogRef.close(result);
  }

  /** Proprietário da embarcação; sem proprietário, o primeiro cliente vinculado (como no legado). */
  private responsibleCustomer(): any {
    const boatCustomers = this.boat?.boatCustomers || [];
    const owner = boatCustomers.find((bc: any) => bc?.type === 'Owner' && bc.customer?.id);
    const first = boatCustomers.find((bc: any) => bc?.customer?.id);
    return (owner || first)?.customer || null;
  }

  private toLocalDateTime(date: Date | string): string {
    return date ? moment(date).utc(true).format('YYYY-MM-DDTHH:mm') : null;
  }

  private errorMessage(err: any, fallback: string): string {
    const message = err?.error?.data?.exception?.message || err?.error?.message
      || (err instanceof Error ? err.message : null);
    return message && String(message).trim() !== '' ? message : fallback;
  }
}
